import { useCallback, useEffect, useRef, useState } from 'react'
import AvatarEditor from './AvatarEditor'
import { AvatarIcon } from './AvatarIcon'
import { parseAvatarConfig, type AvatarConfig } from './avatar-catalog'
import { fetchDonorCampaignAvatar, saveDonorAvatar } from './avatar-api'
import { parseAvatarPiece, type AvatarPiece } from './avatar-custom'
import { DonorAvatar } from './DonorAvatar'
import styles from './ProfileAvatar.module.css'

type Props = {
  avatar?: unknown
  photoUrl: string | null
  name: string
  onSaved: (avatar: AvatarConfig) => void
  onResolved?: (avatar: AvatarConfig | null) => void
  onCompleteProfile: () => void
  campaignId?: number
  openSignal?: number
  isProfileComplete: boolean
}

export function ProfileAvatar({
  avatar,
  name,
  onSaved,
  onResolved,
  onCompleteProfile,
  campaignId,
  openSignal = 0,
  isProfileComplete,
}: Props) {
  const [open, setOpen] = useState(false)
  const [saved, setSaved] = useState(false)
  const [showRequirement, setShowRequirement] = useState(false)
  const [requirementCountdown, setRequirementCountdown] = useState(5)
  const [requirementClosing, setRequirementClosing] = useState(false)
  const lastOpenSignal = useRef(openSignal)
  const requirementCloseTimeout = useRef<number | null>(null)
  const [catalog, setCatalog] = useState<{
    campaignId: number
    avatar: AvatarConfig | null
    items: AvatarPiece[]
    institutionName?: string
    salesOpen?: boolean
  } | null>(null)
  const [catalogError, setCatalogError] = useState<string | null>(null)
  const [retry, setRetry] = useState(0)
  const loadedCampaign = useRef<number | null>(null)
  const fallbackConfig = parseAvatarConfig(avatar)
  const config =
    campaignId && catalog?.campaignId === campaignId ? catalog.avatar : fallbackConfig

  useEffect(() => {
    if (!campaignId || !isProfileComplete || loadedCampaign.current === campaignId) return
    const controller = new AbortController()
    queueMicrotask(() => {
      if (!controller.signal.aborted) {
        setCatalogError(null)
      }
    })
    fetchDonorCampaignAvatar(campaignId, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return
        loadedCampaign.current = campaignId
        setCatalog({
          campaignId,
          avatar: parseAvatarConfig(result.avatar),
          items: result.items
            .map(parseAvatarPiece)
            .filter((piece): piece is AvatarPiece => piece !== null),
          institutionName: result.institution_name,
          salesOpen: result.sales_open,
        })
        onResolved?.(parseAvatarConfig(result.avatar))
      })
      .catch(() => {
        if (!controller.signal.aborted)
          setCatalogError(
            'Não foi possível carregar as peças da campanha. Tente novamente.'
          )
      })
    return () => controller.abort()
  }, [campaignId, isProfileComplete, retry, onResolved])
  const action = !isProfileComplete
    ? 'Avatar bloqueado: complete seu perfil'
    : config
      ? 'Editar meu avatar'
      : 'Criar meu avatar'

  const showProfileRequirement = useCallback(() => {
    if (requirementCloseTimeout.current !== null) {
      window.clearTimeout(requirementCloseTimeout.current)
      requirementCloseTimeout.current = null
    }
    setRequirementClosing(false)
    setRequirementCountdown(5)
    setShowRequirement(true)
  }, [])

  const closeProfileRequirement = useCallback(() => {
    if (requirementCloseTimeout.current !== null) return
    setRequirementClosing(true)
    requirementCloseTimeout.current = window.setTimeout(() => {
      setShowRequirement(false)
      setRequirementClosing(false)
      requirementCloseTimeout.current = null
    }, 180)
  }, [])

  useEffect(() => {
    if (openSignal === lastOpenSignal.current) return
    lastOpenSignal.current = openSignal
    queueMicrotask(() => {
      if (isProfileComplete) {
        setShowRequirement(false)
        setOpen(true)
      } else {
        showProfileRequirement()
      }
    })
  }, [openSignal, isProfileComplete, showProfileRequirement])

  useEffect(() => {
    if (!showRequirement) return
    const timeout = window.setTimeout(() => {
      if (requirementCountdown <= 1) {
        closeProfileRequirement()
      } else {
        setRequirementCountdown((countdown) => countdown - 1)
      }
    }, 1000)
    return () => window.clearTimeout(timeout)
  }, [showRequirement, requirementCountdown, closeProfileRequirement])

  useEffect(
    () => () => {
      if (requirementCloseTimeout.current !== null) {
        window.clearTimeout(requirementCloseTimeout.current)
      }
    },
    []
  )

  useEffect(() => {
    if (!saved) return
    const timeout = window.setTimeout(() => setSaved(false), 2500)
    return () => window.clearTimeout(timeout)
  }, [saved])

  return (
    <div className={styles.container}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => {
          setSaved(false)
          if (isProfileComplete) {
            setShowRequirement(false)
            setOpen(true)
          } else {
            showProfileRequirement()
          }
        }}
        aria-label={action}
        aria-haspopup={isProfileComplete ? 'dialog' : undefined}
      >
        <span
          className={`${styles.picture} ${styles.pictureIllustrated} ${!isProfileComplete ? styles.pictureLocked : ''}`}
        >
          <DonorAvatar config={config} label={`Avatar de ${name}`} />
        </span>
        <span
          className={`${styles.editMark} ${!isProfileComplete ? styles.lockMark : ''}`}
          aria-hidden
        >
          {isProfileComplete ? <AvatarIcon name="pencil" /> : <span>🔒</span>}
        </span>
        <span className={styles.label}>
          {isProfileComplete
            ? config
              ? 'Editar avatar'
              : 'Criar avatar'
            : 'Avatar bloqueado'}
        </span>
      </button>
      {!isProfileComplete && showRequirement ? (
        <div
          role="status"
          className={`${styles.feedback} ${requirementClosing ? styles.feedbackClosing : ''}`}
        >
          <span className={styles.feedbackCountdown}>
            Fecha em {requirementCountdown}s
          </span>
          <p>Complete e salve seu perfil para liberar o avatar personalizado.</p>
          <button
            type="button"
            className={styles.feedbackAction}
            onClick={() => {
              closeProfileRequirement()
              onCompleteProfile()
            }}
          >
            Completar cadastro
          </button>
        </div>
      ) : saved ? (
        <span role="status" className={`${styles.feedback} ${styles.feedbackSaved}`}>
          Avatar salvo!
        </span>
      ) : null}
      {catalogError && isProfileComplete && (
        <div role="alert" className={styles.feedback}>
          {catalogError}{' '}
          <button
            type="button"
            onClick={() => {
              loadedCampaign.current = null
              setRetry((value) => value + 1)
            }}
          >
            Tentar novamente
          </button>
        </div>
      )}
      {open && isProfileComplete && (
        <AvatarEditor
          campaignId={campaignId}
          institutionName={catalog?.institutionName}
          salesOpen={catalog?.salesOpen}
          onPurchased={
            campaignId
              ? async (itemId) => {
                  const result = await fetchDonorCampaignAvatar(campaignId)
                  const items = result.items
                    .map(parseAvatarPiece)
                    .filter((piece): piece is AvatarPiece => piece !== null)
                  const acquired = items.find(
                    (piece) => piece.id === itemId && piece.owned
                  )
                  if (!acquired)
                    throw new Error('A peça ainda não foi liberada. Consulte novamente.')
                  setCatalog((current) =>
                    current?.campaignId === campaignId
                      ? {
                          ...current,
                          items,
                          institutionName: result.institution_name,
                          salesOpen: result.sales_open,
                        }
                      : current
                  )
                  return acquired
                }
              : undefined
          }
          initialAvatar={config}
          campaignItems={campaignId === catalog?.campaignId ? catalog?.items : undefined}
          canSave={!campaignId || catalog?.campaignId === campaignId}
          catalogError={catalogError}
          onRetryCatalog={() => {
            loadedCampaign.current = null
            setRetry((value) => value + 1)
          }}
          onClose={() => setOpen(false)}
          onSave={async (draft) => {
            const updated = await saveDonorAvatar(draft, campaignId)
            if (campaignId)
              setCatalog((current) =>
                current?.campaignId === campaignId
                  ? { ...current, avatar: updated }
                  : current
              )
            onSaved(updated)
            setSaved(true)
          }}
        />
      )}
    </div>
  )
}
